package com.essntl.features.proposal.ui.allproposal.components

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import com.essntl.features.proposal.domain.model.ProposalFilterOptions
import com.essntl.features.proposal.domain.model.ProposalModes
import com.essntl.features.proposal.ui.allproposal.AllProposalNavigation
import com.essntl.features.proposal.ui.allproposal.AllProposalState
import com.essntl.uikit.components.button.primary.EsLargePrimaryButton
import com.essntl.uikit.components.icons.EsIcons
import com.essntl.uikit.components.icons.EsLargeIcon
import com.essntl.uikit.components.input.EsInput
import com.essntl.uikit.components.toggle.EsTab
import com.essntl.uikit.components.toggle.TabState
import com.essntl.uikit.components.utils.EsConstants
import com.essntl.uikit.components.utils.spacing
import com.essntl.uikit.theme.esColorScheme
import com.essntl.uikit.theme.esTypography

@Suppress("UnusedReceiverParameter")
@Composable
fun ColumnScope.AllProposalHeader(
    state: AllProposalState,
    onNavigation: (AllProposalNavigation) -> Unit,
    isSidePanelOpen: Boolean,
    tabItems: List<TabState>,
    onAddProposal: () -> Unit,
    onFilterOptionsChange: (ProposalFilterOptions) -> Unit,
    modifier: Modifier = Modifier,
) {
    Row(
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.SpaceBetween,
        modifier = modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(EsConstants.SectionBorderRadius))
            .background(MaterialTheme.esColorScheme.bgSecondary)
            .padding(
                vertical = EsConstants.SectionVerticalPadding,
                horizontal = EsConstants.SectionHorizontalPadding,
            ),
    ) {
        Row {
            Text(
                text = "Proposals",
                color = MaterialTheme.esColorScheme.textPrimary,
                style = MaterialTheme.esTypography.h2Bold32,
            )
            Spacer(modifier = modifier.width(2.spacing))
            EsTab(
                items = tabItems,
                selectedItemIndex = state.filterOptions.selectedProposalsMode,
                onClick = { tabState ->
                    if (tabState.index == state.filterOptions.selectedProposalsMode) return@EsTab
                    onFilterOptionsChange(
                        state.filterOptions.copy(
                            selectedProposalsMode = tabState.index,
                        ),
                    )
                    onNavigation(AllProposalNavigation.CloseSidePanel)
                },
            )
        }

        EsInput(
            value = state.filterOptions.searchText,
            onValueChange = {
                onFilterOptionsChange(
                    state.filterOptions.copy(
                        searchText = it,
                    ),
                )
            },
            prefix = {
                EsLargeIcon(
                    res = EsIcons.Search,
                )
            },
            placeholder = "Search",
            modifier = Modifier
                .width(268.dp),
        )

        Row(
            verticalAlignment = Alignment.CenterVertically,
        ) {
            ProposalFilterSection(
                state = state,
                onFilterOptionsChange = onFilterOptionsChange,
            )

            if (!isSidePanelOpen) {
                Spacer(Modifier.width(1.spacing))
                EsLargePrimaryButton(
                    onClick = {
                        onAddProposal()
                    },
                    text = "Add proposal",
                    suffixIconRes = EsIcons.Add,
                )
            }
        }
    }

    Spacer(modifier = Modifier.height(1.spacing))

    Row(
        verticalAlignment = Alignment.CenterVertically,
        modifier = Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(EsConstants.RoundedCornerSize))
            .background(MaterialTheme.esColorScheme.bgSecondary)
            .padding(
                vertical = EsConstants.ListRowVerticalPadding,
                horizontal = EsConstants.ListRowHorizontalPadding + EsConstants.SectionHorizontalPadding,
            )
            .height(EsConstants.ListColumnHeaderHeight),
    ) {
        // Proposal information
        ProposalRowDefaultItem(
            text = "Proposal information",
            isFirst = true,
            modifier = Modifier.weight(2f),
        )

        // Owner
        ProposalRowDefaultItem(
            text = "Owner",
            modifier = Modifier.width(80.dp),
        )

        // Traveller
        if (state.filterOptions.selectedProposalsMode == ProposalModes.Leads.index)
            ProposalRowDefaultItem(
                text = "Traveller",
            )

        // Area
        ProposalRowDefaultItem(
            text = "Area",
        )

        // Dates
        ProposalRowDefaultItem(
            text = "Date",
        )

        // Status
        ProposalRowDefaultItem(
            text = "Status",
        )

        // Travel Agent
        if (state.filterOptions.selectedProposalsMode == ProposalModes.Leads.index)
            ProposalRowDefaultItem(
                text = "Travel Agent",
            )

        // PAX
        if (state.filterOptions.selectedProposalsMode == ProposalModes.Leads.index)
            ProposalRowDefaultItem(
                text = "Pax",
                modifier = Modifier.weight(0.5f),
            )

        if (state.filterOptions.selectedProposalsMode == ProposalModes.Designer.index) {
            // Version history
            ProposalRowDefaultItem(
                text = "Version",
                modifier = Modifier.weight(0.5f),
            )

            // Actions
            ProposalRowDefaultItem(
                text = "Actions",
            )
        }

        // Created
        ProposalRowDefaultItem(
            text = "Created",
            modifier = Modifier.weight(0.7f),
        )

        // Modified
        ProposalRowDefaultItem(
            text = "Modified",
            modifier = Modifier.weight(0.7f),
        )

        // Received on Date
        ProposalRowDefaultItem(
            text = "Received on",
            modifier = Modifier.weight(0.7f),
        )
    }
}

@Composable
private fun RowScope.ProposalRowDefaultItem(
    text: String?,
    isFirst: Boolean = false,
    modifier: Modifier = Modifier.weight(1f),
) {
    ProposalRowItem(
        isFirst = isFirst,
        modifier = modifier,
    ) {
        Text(
            text = text ?: "",
            color = MaterialTheme.esColorScheme.textPrimary,
            style = MaterialTheme.esTypography.p2Medium14,
            maxLines = 1,
            overflow = TextOverflow.Ellipsis,
        )
    }
}